import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api, type KeyFields, type Me } from "./api";
import { businessUnitName, divisionName } from "./DivisionPicker";
import { money } from "./format";
import { useForgeEvent } from "./hooks";

export const RESET_LABEL: Record<string, string> = { "1d": "daily", "7d": "weekly", "30d": "monthly" };
const TAG = /^[a-z0-9]+(-[a-z0-9]+)*: [a-z0-9]+(-[a-z0-9]+)*$/;

/** Must match server/src/tags.ts normalizeTag: any spacing and case in, `name: value` out. */
export function normalizeTag(t: string): string {
  const i = t.indexOf(":");
  if (i < 0) return t.trim().toLowerCase();
  const part = (s: string) => s.trim().toLowerCase().replace(/\s+/g, "-");
  return `${part(t.slice(0, i))}: ${part(t.slice(i + 1))}`;
}

export function parseTags(text: string): { tags: string[]; error: string | null } {
  const tags = [...new Set(text.split(",").map((t) => t.trim()).filter(Boolean).map(normalizeTag))];
  const bad = tags.find((t) => !TAG.test(t));
  if (bad) return { tags, error: `"${bad}" needs a name and a value, like project:billing-rewrite (letters, digits, dashes)` };
  if (tags.some((t) => t.startsWith("division: ") || t.startsWith("business-unit: "))) {
    return { tags, error: "Division and business unit come from your record" };
  }
  if (tags.length > 5) return { tags, error: "At most five extra tags" };
  return { tags, error: null };
}

/** Models, budget, reset and extra tags: the same fields for creating and editing a key. */
export function KeyForm({ me, value, onChange }: {
  me: Me;
  value: KeyFields & { tagText: string };
  onChange: (v: KeyFields & { tagText: string }) => void;
}) {
  const models = useQuery({ queryKey: ["models"], queryFn: api.models });
  const modelSel = useRef<HTMLElement>(null);
  const resetSel = useRef<HTMLElement>(null);
  const latest = useRef(value);
  latest.current = value;
  useForgeEvent(modelSel, "change", (e) => {
    const v = (e.target as unknown as { value: unknown }).value;
    onChange({ ...latest.current, models: Array.isArray(v) ? v.map(String) : [] });
  });
  useForgeEvent(resetSel, "change", (e) => {
    const v = String((e.target as HTMLInputElement).value ?? "");
    onChange({ ...latest.current, budgetDuration: (v || null) as KeyFields["budgetDuration"] });
  });
  const [budgetText, setBudgetText] = useState(value.maxBudget === null ? "" : String(value.maxBudget));
  const userBudget = "maxBudget" in me.user ? me.user.maxBudget : null;
  const tagCheck = parseTags(value.tagText);
  const labels = me.directory
    ? [divisionName(me.taxonomy, me.directory.division), businessUnitName(me.taxonomy, me.directory.businessUnit)]
    : [];

  return (
    <>
      <forge-select label-position="block-start" ref={modelSel} label="Models" multiple placeholder="All models" value={value.models}>
        {(models.data ?? []).map((m) => <forge-option key={m} value={m}>{m}</forge-option>)}
      </forge-select>

      <div className="row gap-medium wrap" style={{ alignItems: "flex-start" }}>
        <forge-text-field label-position="block-start" style={{ flex: "1 1 160px" }}>
          <label htmlFor="key-budget">Budget (USD)</label>
          <span slot="start">$</span>
          <input id="key-budget" inputMode="decimal" value={budgetText} placeholder="No key limit"
            onChange={(e) => {
              setBudgetText(e.target.value);
              const n = e.target.value.trim() === "" ? null : Number(e.target.value);
              onChange({ ...latest.current, maxBudget: n !== null && Number.isFinite(n) ? n : null,
                budgetDuration: n === null ? null : latest.current.budgetDuration });
            }} />
          <span slot="support-text">
            {userBudget !== null ? `Your personal budget of ${money(userBudget)} always applies.` : "Blank means no limit on this key."}
          </span>
        </forge-text-field>
        <forge-select label-position="block-start" ref={resetSel} label="Budget resets" value={value.budgetDuration ?? ""}
          disabled={value.maxBudget === null || undefined} style={{ flex: "1 1 160px" }}>
          <forge-option value="">Never</forge-option>
          <forge-option value="1d">Daily</forge-option>
          <forge-option value="7d">Weekly</forge-option>
          <forge-option value="30d">Monthly</forge-option>
        </forge-select>
      </div>

      <div className="stack gap-xsmall">
        <span className="forge-typography--label2">Tags</span>
        <span className="row gap-xsmall wrap">
          {labels.map((l) => <span key={l} className="tag-pill">{l}</span>)}
          <span className="muted forge-typography--body1">set from your record</span>
        </span>
        <forge-text-field label-position="block-start" invalid={!!tagCheck.error || undefined}>
          <label htmlFor="key-tags">Extra tags (optional)</label>
          <input id="key-tags" value={value.tagText} placeholder="project:billing-rewrite, env:dev"
            onChange={(e) => onChange({ ...latest.current, tagText: e.target.value, tags: parseTags(e.target.value).tags })} />
          <span slot="support-text">{tagCheck.error ?? "Comma-separated name:value pairs, up to five. They appear in spend reports."}</span>
        </forge-text-field>
        {!tagCheck.error && tagCheck.tags.length > 0 && (
          <span className="row gap-xsmall wrap" aria-label="Tags as they will be saved">
            <span className="muted forge-typography--body1">Saved as</span>
            {tagCheck.tags.map((t) => <span key={t} className="tag-pill">{t}</span>)}
          </span>
        )}
      </div>
    </>
  );
}
